import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiMapPin, FiChevronDown, FiHelpCircle } = FiIcons;

const Contact = ({ data, personal }) => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const contactInfo = [
    {
      icon: FiMail,
      label: "Email",
      value: personal.email,
      href: `mailto:${personal.email}`,
      color: "from-amber-600 to-amber-700"
    },
    {
      icon: FiPhone,
      label: "Phone",
      value: personal.phone,
      href: `tel:${personal.phone}`,
      color: "from-stone-600 to-stone-700"
    },
    {
      icon: FiMapPin,
      label: "Address",
      value: personal.location,
      href: "#",
      color: "from-amber-700 to-amber-800"
    }
  ];

  const faqs = data.faqs || [];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-stone-800 mb-6">
            {data.title}
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            {data.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg">
              <h3 className="text-2xl font-bold text-stone-800 mb-6">Contact Information</h3>
              <div className="space-y-6">
                {contactInfo.map((contact, index) => (
                  <motion.a
                    key={index}
                    href={contact.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className="flex items-center space-x-4 p-4 rounded-xl bg-stone-50 hover:bg-stone-100 transition-all duration-300 group"
                  >
                    <div className={`w-12 h-12 bg-gradient-to-br ${contact.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <SafeIcon icon={contact.icon} className="text-white text-lg" />
                    </div>
                    <div>
                      <h4 className="text-stone-800 font-semibold">{contact.label}</h4>
                      <p className="text-stone-600 text-sm">{contact.value}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiHelpCircle} className="text-2xl text-amber-700" />
                <h3 className="text-2xl font-bold text-stone-800">{data.faqTitle}</h3>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-stone-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
                    >
                      <span className="text-left font-semibold text-stone-800">{faq.question}</span>
                      <motion.div
                        animate={{ rotate: openFAQ === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <SafeIcon icon={FiChevronDown} className="text-stone-600" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {openFAQ === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 bg-white text-stone-600">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;